import {
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  Select,
  intoSnakeCase,
  searchQueryString,
} from '../../fetchers/_helpers';
import { CommentFields, Comment } from '../../models/components/schemas/comment/Comment';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoCommentFields = readonly CommentFields[];

/** Options for GET /videos/{video_id}/comments/{comment_id} */
export type GetVideoCommentOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.comment+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the comment.
     */
    commentId: number;
  };
};

/** Result type for GET /videos/{video_id}/comments/{comment_id} */
export type GetVideoCommentResult<Paths extends readonly string[]> = Select<Comment, Paths>;

/**
 * Get a specific video comment
 * This method returns the specified video comment.
 */
export async function getVideoComment<Paths extends GetVideoCommentFields>({
  baseUrl,
  select,
  where: { videoId, commentId },
  ...options
}: GetVideoCommentOptions<Paths>): Promise<GetVideoCommentResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/comments/${commentId}?fields=${select
      .map(intoSnakeCase)
      .join(',')}`,
    {
      ...options,
      method: 'GET',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as GetVideoCommentResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type DeleteVideoCommentFields = readonly string[];

/** Options for DELETE /videos/{video_id}/comments/{comment_id} */
export type DeleteVideoCommentOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/comments/{comment_id} */
  query?: {
    /**
     * The hashed password of the password-protected video.
     */
    password?: string;
  };
};

/** Result type for DELETE /videos/{video_id}/comments/{comment_id} */
export type DeleteVideoCommentResult<Paths extends readonly string[]> = unknown;

/**
 * Delete a video comment
 * This method deletes the specified video comment. The authenticated user must be the owner of the comment.
 */
export async function deleteVideoComment<Paths extends DeleteVideoCommentFields>({
  baseUrl,
  where: { videoId, commentId },
  query,
  ...options
}: DeleteVideoCommentOptions<Paths>): Promise<DeleteVideoCommentResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/comments/${commentId}?${searchQueryString(query)}`,
    {
      ...options,
      method: 'DELETE',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as DeleteVideoCommentResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type PatchVideoCommentFields = readonly CommentFields[];

/** Options for PATCH /videos/{video_id}/comments/{comment_id} */
export type PatchVideoCommentOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.comment+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/comments/{comment_id} */
  query?: {
    /**
     * The hashed password of the password-protected video.
     */
    password?: string;
  };

  /** Data serialized to body for PATCH /videos/{video_id}/comments/{comment_id} */
  variables?: {
    /**
     * The rich comment in JSON stringified form. Either this field or the **text** field is required.
     * @example {"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"I love this!"}]}]}
     */

    richtext?: string;

    /**
     * The comment as plain text. Either this field or the **richtext** field is required.
     * @example I love this!
     */

    text?: string;
  };
};

/** Result type for PATCH /videos/{video_id}/comments/{comment_id} */
export type PatchVideoCommentResult<Paths extends readonly string[]> = Select<Comment, Paths>;

/**
 * Edit a video comment
 * This method edits the specified video comment. The authenticated user must be the owner of the comment.
 */
export async function patchVideoComment<Paths extends PatchVideoCommentFields>({
  baseUrl,
  select,
  variables,
  where: { videoId, commentId },
  query,
  ...options
}: PatchVideoCommentOptions<Paths>): Promise<PatchVideoCommentResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/comments/${commentId}?${searchQueryString(query)}&fields=${select
      .map(intoSnakeCase)
      .join(',')}`,
    {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(deepSnakeCase(variables)),
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as PatchVideoCommentResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}
